/**
 * The Cockpit's summary arithmetic — audit v6 V6-01, V6-02, V6-03 and the page's headline rule.
 *
 * Pure, so every figure is asserted on rows rather than read off a render. Every number here is
 * computed from a read the Cockpit ALREADY makes (leader ruling 1: nothing from the design
 * documents), over the whole population that read returns — the proposal book and the position run
 * are the store's own sums (the run digest, the residual distribution; audit 3), the price actions
 * and the candidate queue are one run's whole book (ruling 3).
 *
 * **What the designer drew and this does not compute.** "Elasticity fitted 38.7%" and "61%
 * elasticity fits refused" need the elasticity read, which the Cockpit does not make; "336
 * options hold half the residual stock" is V6-07's Pareto, whose count the platform publishes
 * (P3-3) and which is not derived a second time here. Both are the in-season and exit screens'.
 */

import type {
  ConsolidationResidualDistribution,
  ConsolidationRunDigestSummary,
  DecisionSummary,
  GateD2EvidenceRow,
  MoveRunSummary,
  PendingValue,
  PriceStepBookDigest,
  ProposalBookDigest,
  StoredCandidateQueue,
} from '@/api/generated';
import { consolidationRunDoorsBrokenHref } from '@/features/exit/kpi-links';
import { hasField } from '@/features/inseason/not-published';
import { formatCompactNumber, displayNumberFormat } from '@/lib/format';
import { defineHeadline, type ReadState } from '@/planning';

// ── V6-01 · the four figures ─────────────────────────────────────────────────

export interface ExitFigures {
  readonly residualUnits: number;
  readonly options: number;
  readonly doors: number;
  readonly brokenDoors: number;
}

/**
 * The whole of one position run, from the two reads that already add it up in the store
 * (audit 3): the residual distribution (`total_options`, `total_residual_units` — "over every
 * option of the run, never over the pages a screen has read") and the run digest's residual (the
 * run's door rows, `doors` and `broken_run_doors`, which are the option roll-up's Σ `doors` and
 * Σ `doors_with_broken_run`). It replaces a walk of the roll-up, 16 pages of 500 on CO. `null` for
 * an empty run (ghost ≠ zero), or when either read is not in hand.
 */
export function exitFigures(
  distribution: ConsolidationResidualDistribution | undefined,
  digest: ConsolidationRunDigestSummary | undefined,
): ExitFigures | null {
  if (distribution === undefined || digest === undefined || digest.residual === null) return null;
  if (distribution.total_options === 0) return null;
  return {
    residualUnits: distribution.total_residual_units,
    options: distribution.total_options,
    doors: digest.residual.doors,
    brokenDoors: digest.residual.broken_run_doors,
  };
}

/**
 * Where the Cockpit's broken door positions are listed (ADR 9069): the run-wide doors page, `?runIsBroken=true`, opened under the
 * consolidation run that is PAIRED with the position run the figure counts.
 *
 * The figure is the position run's (`exitFigures`: the run digest's `broken_run_doors`, the option roll-up's Σ
 * `doors_with_broken_run`), while the list is read through a consolidation run — the planner's run, whose directory row names the
 * position run it is paired with. The link is drawn only when the Cockpit can PROVE that pairing from the rows it already
 * holds: the newest consolidation run (the one the lanes tile resolves) names `positionRunId` as its position run, and the URL
 * has not moved the lanes run elsewhere. Otherwise there is no link, never a list over another position run than the figure's.
 * The list reads one page and states the platform's count of the same predicate, so the link holds at any size of run.
 */
export function brokenDoorsHref(
  positionRunId: string | null,
  newestDecisionRun: { readonly run_id: string; readonly position_run_id?: string | null } | undefined,
  chosenDecisionRunId: string | null,
  brokenPositions: number | null,
): string | null {
  if (positionRunId === null || newestDecisionRun === undefined || brokenPositions === null) return null;
  if (newestDecisionRun.position_run_id !== positionRunId) return null;
  if (chosenDecisionRunId !== null && chosenDecisionRunId !== newestDecisionRun.run_id) return null;
  return consolidationRunDoorsBrokenHref(newestDecisionRun.run_id);
}

/**
 * The steps still to decide: nobody has recorded a disposition and no newer run replaced them —
 * the decision summary's `price_actions_pending` (ADR 6036) with the two figures that name them,
 * `price_actions_pending_articles` and `price_actions_pending_first_week` (ADR 6173). All three use
 * the platform's one pending test, so they cannot disagree. A figure the running platform does not
 * publish is `null`, never 0.
 */
export interface PendingPriceFigures {
  readonly steps: number;
  readonly articles: number | null;
  /** The earliest effective week among the pending steps; `null` when none is pending (or not published). */
  readonly firstWeek: string | null;
}

export interface PriceFigures {
  /** Every step of the run, decided or not — the run's book. */
  readonly steps: number;
  readonly articles: number;
  /** What is left to decide in that book (cockpit KPI «Price steps to decide»). */
  readonly pending: PendingPriceFigures;
  /** The earliest effective week of the book — ISO weeks sort as text; `null` when not published. */
  readonly firstWeek: string | null;
  /** How many distinct weeks the book spans; `null` when not published. */
  readonly weeks: number | null;
  /**
   * Σ expected margin recovery over the run's PATHS, each once, only in one currency: the digest's
   * `expected_margin_recovery`, which counts a path's total once, on its first step. `null` when the
   * book is in two currencies — never summed across them.
   */
  readonly recovery: { readonly amount: number; readonly currency: string } | null;
}

/**
 * The run's price steps from the two reads that add them up in the store (audit 4, ADR 6173): the
 * run digest's `price_steps` book (steps, articles, weeks, first week, recovery) and the decision
 * summary's pending figures. It replaces a walk of every step of the run (`GET /inseason/price-actions`,
 * hundreds of rows a page) — and, before that, ten such walks to tell a price run from the cycle's
 * others. `null` for a run with no step, or when either read is not in hand.
 */
export function priceFigures(
  book: PriceStepBookDigest | undefined,
  summary: DecisionSummary | undefined,
): PriceFigures | null {
  if (book === undefined || summary === undefined || book.price_steps === 0) return null;
  const recovery = book.expected_margin_recovery.length === 1 ? book.expected_margin_recovery[0] ?? null : null;
  return {
    steps: book.price_steps,
    articles: book.articles,
    pending: {
      steps: summary.price_actions_pending,
      articles: summary.price_actions_pending_articles ?? null,
      firstWeek: summary.price_actions_pending_first_week ?? null,
    },
    firstWeek: book.first_week ?? null,
    weeks: book.weeks ?? null,
    recovery: recovery === null ? null : { amount: recovery.amount, currency: recovery.currency_code },
  };
}

export interface CandidateFigures {
  readonly considered: number;
  readonly solved: number;
  readonly refused: number;
}

/** The run's candidate queue, ranked and unranked alike. `refused` is every unsolved path. */
export function candidateFigures(queue: StoredCandidateQueue | undefined): CandidateFigures | null {
  if (queue === undefined) return null;
  const all = [...queue.ranked, ...queue.unranked];
  if (all.length === 0) return null;
  const solved = all.filter((candidate) => candidate.path_solved).length;
  return { considered: all.length, solved, refused: all.length - solved };
}

/** A part over its whole as a percentage, or `null` when there is no whole. */
export function percentOf(part: number, whole: number): number | null {
  return whole > 0 ? (part / whole) * 100 : null;
}

// ── ADR 9015 · every use case, from a read the platform serves ───────────────

export interface ReplenishmentFigures {
  /** The lines that propose something: the run's `served_lines` (bata-platform ADR 6183), the markers apart. */
  readonly proposals: number;
  /** The stores with at least one such line. */
  readonly stores: number;
  /** The stores the minimum size run dropped (nothing but markers); 0 on a digest older than the split. */
  readonly droppedStores: number;
  readonly articles: number;
  /** Σ `units` — what the run proposed to ship. */
  readonly units: number;
  /** Σ `requested_units` — what the doors asked for (the platform's `need_units`). */
  readonly need: number;
}

/**
 * The newest run's pull and push proposals, from the run digest's proposal book (audit 3): the
 * same lines `GET /inseason/proposals` pages, added up in the store (bata-platform ADR 6039) —
 * the Cockpit used to walk those pages, 32 requests of 1000 on CO, to add them again here.
 *
 * `requested_units` is the need: the job writes it from `ask.need_units` (bata-platform
 * `services/jobs/inseason.py`), and it is `NOT NULL`, so every proposal carries both halves of
 * the fill rate and the ratio needs no guess. `null` for an empty run — a ghost, never a zero.
 */
export function replenishmentFigures(book: ProposalBookDigest | undefined): ReplenishmentFigures | null {
  if (book === undefined || book.proposals === 0) return null;
  return {
    proposals: book.served_lines ?? book.proposals,
    stores: book.served_doors ?? book.stores,
    droppedStores: book.dropped_doors ?? 0,
    articles: book.served_articles ?? book.articles,
    units: book.units,
    need: book.requested_units,
  };
}

/** A move run's status when it refused to run (`MoveRunSummary.status`): it decided nothing. */
export const MOVE_RUN_REFUSED = 'refused';

/**
 * One move run as the Compacting tile and the moves row state it (bata-reqs ADR 9002 stage 3, platform
 * ADR 9049/9050): the counts the job wrote on its directory row — always numbers, 0 over nothing.
 */
export interface MoveRunFigures {
  readonly runId: string;
  /** The run refused (`status` `refused`, `refusal_code` says why): it decided nothing. */
  readonly refused: boolean;
  readonly refusalCode: string | null;
  /** Lines the run proposed this week. */
  readonly proposed: number;
  /** Lines held back this week (a cap or a joint rule), in their place on the list. */
  readonly held: number;
}

/** A directory row as {@link MoveRunFigures}; `null` with no row. Pure. */
export function moveRunFigures(run: MoveRunSummary | undefined): MoveRunFigures | null {
  if (run === undefined) return null;
  return {
    runId: run.run_id,
    refused: run.status === MOVE_RUN_REFUSED,
    refusalCode: run.refusal_code,
    proposed: Math.max(0, run.proposed_lines),
    held: Math.max(0, run.held_lines),
  };
}

/**
 * The move run the Cockpit states, from the LIVE move-run directory (`GET /compacting/runs`, newest first —
 * the Compacting pages' own cached read): the run the decision summary counts the pending moves of
 * (`transfers_pending_move_run_id`), so the figure, the pending count and the page the tile opens are one
 * run; else the newest run of the market on screen. `undefined` when neither is listed. Pure.
 *
 * Since platform v0.126.0 the in-season cycle writes no transfer book (`inseason.rebalance` retired, one daily
 * `compacting.move_run`), so the cycle run's digest no longer says what Compacting proposed.
 */
export function cockpitMoveRun(
  runs: readonly MoveRunSummary[],
  countedRunId: string | null,
  country: string | null,
): MoveRunSummary | undefined {
  const counted = countedRunId === null ? undefined : runs.find((run) => run.run_id === countedRunId);
  if (counted !== undefined) return counted;
  return country === null ? undefined : runs.find((run) => run.country_key === country && run.simulation_id === null);
}

/** Gate D2 in one market, as the tile states it. */
export interface D2Market {
  readonly country: string;
  readonly assessedOn: string;
  readonly enabled: boolean;
  /** `false` where any lane is uncalibrated: the share is then no evidence, and is not shown. */
  readonly measured: boolean;
  /**
   * `null` where the signed definition makes the share undefined — no lane could be scored
   * (bata-platform ADR 6050). Never 0: a market with nothing to clear did not clear 0 %.
   */
  readonly clearingShare: number | null;
  /** Why the share is absent — the platform's code, verbatim; `null` while it is published. */
  readonly measuresAbsentReason: string | null;
  readonly minimumClearingShare: number;
  readonly holdReasonCode: string | null;
}

/**
 * The NEWEST assessment per market.
 *
 * One rule for both reads the tile can make. `GET /inseason/d2-evidence/latest` (bata-platform
 * ADR 6030) already answers one row per market, its newest; the dated read at a chosen `?asOf=`
 * returns every assessment with `assessed_on <= as_of`, so a market assessed twice is two rows and
 * the gate stood at the later one. Picked by date here rather than by trusting the row order, and
 * sorted by market so the tile reads the same every time. A held market stays HELD —
 * `transfers_enabled` false is carried as it is, never folded into a share or a count of zero
 * (ADR 0052).
 */
export function d2Markets(rows: readonly GateD2EvidenceRow[]): readonly D2Market[] {
  const newest = new Map<string, GateD2EvidenceRow>();
  for (const row of rows) {
    const held = newest.get(row.country_key);
    if (held === undefined || row.assessed_on > held.assessed_on) newest.set(row.country_key, row);
  }
  return [...newest.values()]
    .sort((a, b) => (a.country_key < b.country_key ? -1 : a.country_key > b.country_key ? 1 : 0))
    .map((row) => ({
      country: row.country_key,
      assessedOn: row.assessed_on,
      enabled: row.transfers_enabled,
      measured: row.uncalibrated_lanes === 0,
      clearingShare: row.clearing_share,
      measuresAbsentReason: row.clearing_share === null ? row.measures_absent_reason ?? null : null,
      minimumClearingShare: row.minimum_clearing_share,
      holdReasonCode: row.transfers_enabled ? null : row.hold_reason_code ?? null,
    }));
}

// ── ADR 9015 amendment · the reads bata-platform#406 added (ADR 6030) ────────

/**
 * The problem the run-bound Cockpit reads answer when the run does not exist OR is outside the
 * caller's scope — the platform does not say which, on purpose (a scope check that says "exists,
 * not yours" leaks the run). Spelled here because it is contract vocabulary, as
 * `MOVE_RUN_REFUSED` is.
 */
export const RUN_NOT_FOUND_CODE = 'planning.inseason.run_not_found';

/** Whether a read was refused because its run is unknown or out of scope — not a failure. */
export function isRunNotFound(state: ReadState<unknown>): boolean {
  return state.kind === 'unavailable' && state.status === 404 && state.code === RUN_NOT_FOUND_CODE;
}

/**
 * What still waits on a planner in one run, from `GET /inseason/runs/{run_id}/decision-summary`.
 *
 * `transfersPending` is the platform's own pending count when it says transfer dispositions are
 * recorded (`transfer_dispositions_recorded: true`, platform 0.46.0 onwards, ADR 6031), and `null`
 * otherwise: the count is then unknown, and a `0` — or the total passed off as pending — would be
 * a finding nobody made.
 */
export interface DecisionCounts {
  /** The proposals that move stock — the markers of dropped doors are decisions recorded, not proposals (ADR 6183). */
  readonly proposalsTotal: number;
  /** The markers, apart; 0 on a platform older than the split. */
  readonly proposalsDropped: number;
  readonly proposalsPending: number;
  /**
   * Whether `proposalsPending` is the served count — the lines the pending list draws. On a platform that does not state it
   * the figure is the older count, which includes the dropped-door markers, and no list totals it: the row opens nothing.
   */
  readonly proposalsPendingListed: boolean;
  readonly proposalsDecided: number;
  /** The legacy transfer trail's lines (the book before the one run); no longer the pending queue. */
  readonly transfersTotal: number;
  /**
   * Since platform v0.126.0 (bata-reqs ADR 9002 stage 3): the one run's undecided proposed and held lines,
   * of the run {@link transfersMoveRunId} names. `null` where the platform records no dispositions.
   */
  readonly transfersPending: number | null;
  /** The move run `transfersPending` counts; `null` with a count of 0 when the market has none. */
  readonly transfersMoveRunId: string | null;
  /**
   * ADR 6036's half of the summary — per-queue value at stake, the price-step counts and the
   * open alerts of the run's market — or `null` where the platform answering predates it
   * (production until v0.47): the summary is served, these fields are not on it. `null` is
   * "not published by this platform version", never zeros (`docs/adr/9017`).
   */
  readonly valued: ValuedDecisions | null;
}

/** The value of what still waits, in one currency — or why the platform published none. */
export type DecisionStake =
  | { readonly kind: 'money'; readonly amount: number; readonly currency: string }
  | { readonly kind: 'absent'; readonly reason: 'mixedCurrencies' | 'notPublished' | 'unrecognised'; readonly code: string };

export interface ValuedDecisions {
  readonly priceStepsTotal: number;
  readonly priceStepsPending: number;
  readonly alertsOpen: number;
  readonly proposalsStake: DecisionStake;
  readonly transfersStake: DecisionStake;
  readonly priceStepsStake: DecisionStake;
}

/** The platform's reason for a withheld sum (ADR 6036). */
export const MIXED_CURRENCIES = 'planning.inseason.pending_value.mixed_currencies';

/**
 * One queue's pending value. Money only when the platform published an amount AND its currency:
 * a figure is never shown without the currency it is in, and nothing here sums across two.
 * `nothing_pending` never reaches a row — a queue with nothing pending has no row.
 */
export function decisionStake(value: PendingValue): DecisionStake {
  if (value.amount !== null && value.currency_code !== null && value.currency_code !== '') {
    return { kind: 'money', amount: value.amount, currency: value.currency_code };
  }
  const code = value.absent_reason ?? '';
  return { kind: 'absent', reason: code === MIXED_CURRENCIES ? 'mixedCurrencies' : 'unrecognised', code };
}

/** A stake the running platform does not publish (it predates ADR 6036). */
export const STAKE_NOT_PUBLISHED: DecisionStake = { kind: 'absent', reason: 'notPublished', code: '' };

/** ADR 6036's fields, when the platform answering carries them — tested at runtime (`hasField`). */
export function valuedDecisions(summary: DecisionSummary): ValuedDecisions | null {
  const fields = [
    'alerts_open', 'price_actions_total', 'price_actions_pending',
    'pending_expected_impact', 'pending_transfer_net_benefit', 'pending_price_margin_recovery',
  ];
  if (!fields.every((field) => hasField(summary, field))) return null;
  return {
    priceStepsTotal: summary.price_actions_total,
    priceStepsPending: summary.price_actions_pending,
    alertsOpen: summary.alerts_open,
    proposalsStake: decisionStake(summary.pending_expected_impact),
    transfersStake: decisionStake(summary.pending_transfer_net_benefit),
    priceStepsStake: decisionStake(summary.pending_price_margin_recovery),
  };
}

export function decisionCounts(summary: DecisionSummary | undefined): DecisionCounts | null {
  if (summary === undefined) return null;
  return {
    // The served counts, where the platform states them; else the old ones (they counted the markers).
    proposalsTotal: summary.proposals_served_total ?? summary.proposals_total,
    proposalsDropped: summary.proposals_dropped_door_total ?? 0,
    proposalsPending: summary.proposals_served_pending ?? summary.proposals_pending,
    // The pending list excludes the dropped-door markers; only the served count is its total (ADR 9069).
    proposalsPendingListed: summary.proposals_served_pending !== undefined,
    proposalsDecided: summary.proposals_served_total !== undefined && summary.proposals_served_pending !== undefined
      ? summary.proposals_served_total - summary.proposals_served_pending
      : summary.proposals_decided,
    transfersTotal: summary.transfers_total,
    transfersPending: summary.transfer_dispositions_recorded ? summary.transfers_pending : null,
    transfersMoveRunId: summary.transfers_pending_move_run_id ?? null,
    valued: valuedDecisions(summary),
  };
}

// ── V6-02 · what needs a decision ────────────────────────────────────────────

export type DecisionId =
  | 'refusedCandidates' | 'priceSteps' | 'replenishment' | 'transfers' | 'openAlerts' | 'writeBack'
  | 'ladderExpiry' | 'pricingCoverage';

export interface DecisionRow {
  readonly id: DecisionId;
  readonly count: number;
  readonly values: Readonly<Record<string, string | number>>;
  /**
   * The value at stake, where the kind of decision has one (replenishment, transfers, price
   * steps). Absent — with its reason — where the platform withheld or does not publish it;
   * omitted on a row whose decisions carry no money (alerts, commands).
   */
  readonly stake?: DecisionStake;
  /**
   * The count is one no list totals: the row opens nothing (ADR 9069). Set on replenishment when the platform does not state the
   * served pending count and the figure is the older one, which includes the dropped-door markers the pending lines do not list.
   */
  readonly unlisted?: true;
}

/**
 * At most this many rows — one per kind of decision, so no kind is ever cut off the list.
 *
 * It was the designer's four, when the Cockpit read four kinds of decision and five rows could
 * exist. ADR 9015 added replenishment and transfers at the owner's request; kept at four, a
 * planner with seven kinds waiting would silently never see the last three, and a list that
 * drops a decision without saying so is the failure this list exists to prevent. The eighth is
 * the discount ladders ending soon (owner rulings 2026-10-17, second set, R3.4). The exit lanes'
 * row left with the legacy consolidation writer (bata-reqs ADR 9002 stage 3, owner ruling 2026-11-02):
 * `exit.consolidation_run` writes no lane since platform v0.126.0, and every move is the one run's.
 */
export const DECISION_ROWS = 8;

/**
 * The discount ladders the platform flags as ending soon (`GET /registry/price-ladders`, each
 * version's `expiry.endsSoon`, bata-platform ADR 6189 as amended): how many, and the first end.
 * The platform measures it on the market's date against `markdown.ladder_expiry_warning_days`;
 * nothing here counts days.
 */
export interface LadderExpiryFigures {
  readonly ladders: number;
  /** The earliest `validTo` among them, ISO. */
  readonly firstEnd: string;
}

/**
 * The process calendar's pricing coverage in the run's market (owner, 2026-10-17): the platform's
 * warning on `GET /registry/process-calendar` (`pricingCoverage`) for the market's current year, read
 * on the market's date. A row only when the platform names a warning.
 */
export interface PricingCoverageFigures {
  readonly country: string;
  readonly year: number;
  /** `short` (covered to a day), `uncovered` (no pricing period holds today), `stayNotGoverned`. */
  readonly kind: 'short' | 'uncovered' | 'stayNotGoverned';
  readonly coveredTo: string;
  readonly requiredTo: string;
}

/** Gate D2 in the market of the run on screen, as far as the transfers row needs it. */
export type D2Standing = 'enabled' | 'held' | 'unknown';

/**
 * The decisions, in the order a planner takes them: what cannot be priced at all, then what
 * the cycle priced, then what it proposed to ship and to move, then the end of life, then the
 * platform's own inbox and the write path.
 *
 * A row is listed only when its read has ANSWERED with something to decide. A read still
 * loading, failed or empty contributes no row — "0 things to decide" over a read that failed
 * would be a finding nobody made; the coverage strip beside this list says which reads those are.
 */
export function decisionRows(input: {
  readonly candidates: CandidateFigures | null;
  readonly prices: PriceFigures | null;
  /** The run's decision summary (ADR 6030): what is still PENDING, not the size of the book. */
  readonly decisions?: DecisionCounts | null;
  /** The PRICE run's summary (F16: it may be another run) — the price steps' pending count. */
  readonly priceDecisions?: DecisionCounts | null;
  /** The run's books, for the rows' context line only — never their count. */
  readonly replenishment?: ReplenishmentFigures | null;
  /** The move run the summary counts the pending moves of, from the live directory — context line only. */
  readonly moves?: MoveRunFigures | null;
  /** Gate D2 in the run's market — a held market says so on the transfers row. */
  readonly d2?: D2Standing;
  readonly openAlerts: number | null;
  readonly writeBack: number | null;
  /** The ladders ending soon — `null` while the read has not answered, or answered none. */
  readonly ladderExpiry?: LadderExpiryFigures | null;
  /** The calendar's pricing coverage warning — `null` while unread, covered, or no market. */
  readonly pricingCoverage?: PricingCoverageFigures | null;
}, locale: string): readonly DecisionRow[] {
  const rows: DecisionRow[] = [];
  const compact = (value: number): string => formatCompactNumber(value, locale).text;
  if (input.candidates !== null && input.candidates.refused > 0) {
    rows.push({ id: 'refusedCandidates', count: input.candidates.refused, values: { considered: compact(input.candidates.considered) } });
  }
  // Price steps count what is PENDING where the price run's summary publishes it (ADR 6036),
  // with the pending margin recovery as the stake; otherwise the run's whole book, said so, with
  // the book's recovery — in one currency or withheld, never summed across two.
  const priceValued = input.priceDecisions?.valued ?? null;
  if (input.prices !== null && (priceValued === null || priceValued.priceStepsPending > 0)) {
    const book = input.prices;
    rows.push({
      id: 'priceSteps',
      count: priceValued === null ? book.steps : priceValued.priceStepsPending,
      values: {
        pending: priceValued === null ? 'book' : 'pending',
        total: compact(priceValued?.priceStepsTotal ?? book.steps),
        articles: book.articles,
        week: book.firstWeek ?? '—',
      },
      stake: priceValued !== null
        ? priceValued.priceStepsStake
        : book.recovery === null
          ? { kind: 'absent', reason: 'mixedCurrencies', code: MIXED_CURRENCIES }
          : { kind: 'money', amount: book.recovery.amount, currency: book.recovery.currency },
    });
  }
  const decisions = input.decisions ?? null;
  const valued = decisions?.valued ?? null;
  // Replenishment counts the PENDING proposals: a book whose every proposal is decided waits on nobody.
  if (decisions !== null && decisions.proposalsPending > 0) {
    const book = input.replenishment ?? null;
    const fill = book === null ? null : percentOf(book.units, book.need);
    rows.push({
      id: 'replenishment',
      count: decisions.proposalsPending,
      ...(decisions.proposalsPendingListed ? {} : { unlisted: true as const }),
      values: {
        total: compact(decisions.proposalsTotal),
        decided: compact(decisions.proposalsDecided),
        dropped: decisions.proposalsDropped,
        book: book === null ? 'no' : 'yes',
        units: book === null ? '' : compact(book.units),
        need: book === null ? '' : compact(book.need),
        fill: fill === null ? '' : displayNumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(fill),
        fillKnown: fill === null ? 'no' : 'yes',
      },
      stake: valued?.proposalsStake ?? STAKE_NOT_PUBLISHED,
    });
  }
  // The moves waiting on a planner: the one run's undecided proposed and held lines (platform v0.126.0,
  // bata-reqs ADR 9002 stage 3), of the move run the summary names. The row shows while that run has any —
  // `transfers_total` counts the legacy trail, which no longer grows, so it no longer gates the row.
  if (decisions !== null && decisions.transfersMoveRunId !== null) {
    const pending = decisions.transfersPending;
    if (pending !== null && pending > 0) {
      // The run's own counts, only when the directory row IS the run the summary counted.
      const run = input.moves !== undefined && input.moves !== null && input.moves.runId === decisions.transfersMoveRunId ? input.moves : null;
      rows.push({
        id: 'transfers',
        count: pending,
        values: {
          run: run === null ? 'no' : 'yes',
          proposed: run?.proposed ?? 0,
          held: run?.held ?? 0,
          gate: input.d2 ?? 'unknown',
        },
        stake: valued?.transfersStake ?? STAKE_NOT_PUBLISHED,
      });
    }
  }
  // Alerts: the run's market's open alerts where the summary publishes them (ADR 6036), else the
  // open queue as the caller's scope reads it — each says which it is.
  if (valued !== null) {
    if (valued.alertsOpen > 0) rows.push({ id: 'openAlerts', count: valued.alertsOpen, values: { scope: 'market' } });
  } else if (input.openAlerts !== null && input.openAlerts > 0) {
    rows.push({ id: 'openAlerts', count: input.openAlerts, values: { scope: 'queue' } });
  }
  if (input.writeBack !== null && input.writeBack > 0) rows.push({ id: 'writeBack', count: input.writeBack, values: {} });
  const ending = input.ladderExpiry ?? null;
  if (ending !== null && ending.ladders > 0) {
    rows.push({ id: 'ladderExpiry', count: ending.ladders, values: { first: ending.firstEnd } });
  }
  const coverage = input.pricingCoverage ?? null;
  if (coverage !== null) {
    rows.push({
      id: 'pricingCoverage', count: 1,
      values: { country: coverage.country, kind: coverage.kind, coveredTo: coverage.coveredTo, requiredTo: coverage.requiredTo },
    });
  }
  return rows.slice(0, DECISION_ROWS);
}

// ── V6-03 · what the reads on this page answered ─────────────────────────────

/**
 * How a read answered, as the coverage strip groups it. `notFound` is a run-bound read refused
 * with `planning.inseason.run_not_found`: the run named does not exist or is outside the caller's
 * scope — neither a fault nor a role refusal, and named for what it is (ADR 9015, amended).
 */
export type CoverageKind = 'live' | 'empty' | 'notBuilt' | 'noPermission' | 'notFound' | 'failed' | 'loading';

export const COVERAGE_ORDER: readonly CoverageKind[] = ['live', 'empty', 'notBuilt', 'noPermission', 'notFound', 'failed', 'loading'];

export function coverageKind(state: ReadState<unknown>): CoverageKind {
  switch (state.kind) {
    case 'populated': return 'live';
    case 'awaiting': return 'empty';
    case 'loading': return 'loading';
    default:
      if (isRunNotFound(state)) return 'notFound';
      return state.reason === 'not-built' ? 'notBuilt' : state.reason === 'no-permission' ? 'noPermission' : 'failed';
  }
}

export interface CoverageEntry {
  readonly id: string;
  /** The read, in the planner's words. */
  readonly labelKey: string;
  readonly state: ReadState<unknown>;
  /** How much it answered, when it answered with rows — a count, never for an empty read. */
  readonly count?: number | undefined;
  /** Read it again — what the page strip's «Riprova» calls for a read that failed. */
  readonly retry?: (() => unknown) | undefined;
}

export type CoverageTally = Readonly<Record<CoverageKind, number>>;

export function coverageTally(entries: readonly CoverageEntry[]): CoverageTally {
  const tally: Record<CoverageKind, number> = { live: 0, empty: 0, notBuilt: 0, noPermission: 0, notFound: 0, failed: 0, loading: 0 };
  for (const entry of entries) tally[coverageKind(entry.state)] += 1;
  return tally;
}

// ── the hero's sentence (brief part 1 §6, DEC-87, DEC-96) ────────────────────

export interface CockpitSynthesisData {
  /** True while any read the sentence could count from has not answered. */
  readonly settling: boolean;
  /** False once the run directory answered empty: no cycle has recorded a run. */
  readonly runKnown: boolean;
  readonly decisions: readonly DecisionRow[];
  readonly tally: CoverageTally;
}

/**
 * The value the sentence may name, or none.
 *
 * ADR 9017 rejected summing the rows' values: three queues' values are three measures (expected
 * impact, net benefit, margin recovery), possibly in three currencies. So the sentence names a
 * value only when EXACTLY ONE row carries a value at stake at all, and that value is money the
 * platform published. It then says which decisions the value belongs to — all of them, or "k of
 * them" — so the amount is never read as the worth of rows it does not cover (DEC-96: never
 * estimated, never implied).
 */
export function synthesisValue(rows: readonly DecisionRow[]):
  | { readonly kind: 'none' }
  | { readonly kind: 'money'; readonly amount: number; readonly currency: string; readonly count: number } {
  const staked = rows.filter((row) => row.stake !== undefined);
  const only = staked.length === 1 ? staked[0] : undefined;
  if (only === undefined || only.stake?.kind !== 'money') return { kind: 'none' };
  return { kind: 'money', amount: only.stake.amount, currency: only.stake.currency, count: only.count };
}

/** Money for a sentence: compact above the threshold, in its own currency — never without it. */
export function compactMoney(amount: number, currency: string, locale: string): string {
  return `${formatCompactNumber(amount, locale).text} ${currency}`;
}

/**
 * The hero's title (brief part 1 §6): «7 decisioni aspettano te, per 84 M COP».
 *
 * The count is the sum of the "Needs a decision" list's N column — the figure the planner can
 * check against the table right under it. The value follows `synthesisValue`. The sentence waits
 * while any read is loading (a conclusion over reads in flight would change under the planner),
 * and "nothing waits" is never said over a read that could not be read.
 */
export const cockpitSynthesis = defineHeadline({
  keys: {
    reading: 'overview.hero.reading',
    noRun: 'overview.hero.noRun',
    nothingPublished: 'overview.hero.nothingPublished',
    clear: 'overview.hero.clear',
    clearWithGaps: 'overview.hero.clearWithGaps',
    waiting: 'overview.hero.waiting',
  },
  choose: ({ settling, runKnown, decisions, tally }: CockpitSynthesisData, { locale }) => {
    if (settling) return { variant: 'reading' };
    if (!runKnown) return { variant: 'noRun' };
    const unavailable = tally.failed + tally.noPermission + tally.notFound + tally.notBuilt;
    if (decisions.length === 0) {
      if (tally.live === 0) return { variant: 'nothingPublished' };
      return { variant: unavailable > 0 ? 'clearWithGaps' : 'clear' };
    }
    const count = decisions.reduce((sum, row) => sum + row.count, 0);
    const value = synthesisValue(decisions);
    if (value.kind === 'none') return { variant: 'waiting', values: { count, value: 'none', part: 0, amount: '' } };
    return {
      variant: 'waiting',
      values: {
        count,
        value: value.count === count ? 'all' : 'part',
        part: value.count,
        amount: compactMoney(value.amount, value.currency, locale),
      },
    };
  },
});
